import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Plan } from "./plans";

export type Offer = {
  addon_product_id: string;
  title: string;
  description: string;
  image_name: string;
  price: number;
  regular_price: number;
  per_month: boolean;
  shipping_address_required: boolean;
  is_free_shipping: boolean;
  is_free: boolean;
  is_sold_out: boolean;
  discount: number;
  label: string;
  is_hidden?: boolean;
};

export interface ServerContextState {
  /** In developer mode. Debug param set by &idm=true query param */
  idm?: boolean;

  // Basic serverContext stubbing containing most of the fields we can expect
  // from serverContext.
  // Meta information
  flow_id: string;
  context_type: string;
  currency_symbol: string;
  mixpanel_properties: JsonObject;
  session_cookie_domain: string;

  // User Information
  user_id: string;
  user_in_db: boolean;
  distinct_id: string;
  name: string;
  email: string;
  gdpr_consent: string; // JSON in string

  // URLS
  static_url: string;
  MIXPANEL_PROJECT_TOKEN: string;
  SENTRY_PROJECT_DSN_JS: string;
  PURCHASE_URL?: string;

  // Feature flags
  direct: boolean;
  is_paid_traffic: boolean;
  no_trial_active: boolean;
  trial_fee_waived: boolean;
  trial_fees: number[];
  new_session: boolean;
  choose_trial_fee: boolean;
  popup: any; // TODO: Align with the campaign popup props
  campaign_promo: string;
  promo_code: string;
  campaign_promo_cookie: {
    active: boolean;
    value?: string;
  };

  // Plan information
  plans: Record<string, Plan>;
  noom_plan_id: string;
  hasTrialFee: boolean;
  trialFee: number;
  plan?: Plan;
  fast_forward_trial_plan?: Plan;
  purchased_fast_forward_trial?: boolean;
  plan_override?: string;
  claimed_by_email?: string;

  // https://github.com/noom/growth/blob/develop/growth/flow/flows/en.py#L1
  has_no_trial?: boolean;

  // Counter Offer
  // https://github.com/noom/growth/blob/develop/growth_api/context/counter_offer.py#L124
  // TODO: Can we provide better scoping based on the context that is active?
  // "result": "success",
  // "context_type": "counter-offer",
  foreign_subscription_id?: string;
  plan_duration?: number;
  access_code?: string;
  subscription_id?: string;
  // https://github.com/noom/growth/blob/develop/visitor/constants.py#L23
  subscription_status?:
    | "ACTIVE"
    | "PAST_DUE"
    | "PENDING"
    | "INACTIVE"
    | "FRAUDULENT";
  currency?: Plan["currency"];
  offered_discounted_plan?: Plan;
  accepted_offer_duration?: number;
  is_in_trial?: boolean;
  expiry_time?: string;
  has_accepted_counter_offer?: boolean;
  subscription_is_hm?: boolean;
  counter_offer_price?: string;
  has_expiring_subscription?: boolean;
  upid?: string;
  eligible_for_counter_offer?: boolean;
  // NOTE(sumin)?: Hackily add subscriptions until the frontend doesn't need it anymore.
  subscriptions?: string; // JSON encoded object...

  podcast_options?: Record<string, string>;

  experiment_tag?: string;

  // TODO: Dead code? Could not find in server via grep.
  discount_percent?: string;

  // Addons
  offers?: Record<
    /** channel */ string,
    Record</** placement type */ string, Offer[]>
  >;

  total_price?: string;
  trial_fee?: string;
  sales_tax_amount?: string;
  minimal_discount_fast_forward_trial_plan?: Plan;
  minimal_discount_noom_plan_id?: string;

  has_offers?: boolean;

  show_results_vary_disclaimer?: boolean;

  enrollment?: boolean;

  no_skip?: boolean;

  direct_traffic?: boolean;

  // Course
  display_counter_offer?: boolean;
  display_revert_flow?: boolean;

  plan_id?: string;
  payment_source?: "BRAINTREE" | "STRIPE";
  reason?: string;
  course?: string;
}

const initialState: ServerContextState = {
  // Basic serverContext stubbing containing most of the fields we can expect
  // from serverContext.
  // Meta information
  flow_id: "",
  context_type: "",
  currency_symbol: "$",
  mixpanel_properties: {},
  session_cookie_domain: ".noom.com",

  // User Information
  user_id: "",
  user_in_db: false,
  distinct_id: "",
  name: "",
  email: "",
  gdpr_consent: "",

  // URLS
  static_url: "/static",
  MIXPANEL_PROJECT_TOKEN: "",
  SENTRY_PROJECT_DSN_JS: "",

  // Feature flags
  direct: false,
  is_paid_traffic: false,
  no_trial_active: false,
  trial_fee_waived: false,
  trial_fees: [],
  new_session: true,
  choose_trial_fee: true,
  popup: null,
  campaign_promo: "",
  promo_code: "",
  campaign_promo_cookie: {
    active: false,
  },

  // Plan information
  noom_plan_id: "",
  hasTrialFee: false,
  trialFee: 0,
  plan: {
    batch_symbol: null,
    braintree_id: "",
    currency: "",
    currency_symbol: "",
    discounted_plan_price: 0,
    discounts: {},
    billing_cycle_in_months: 0,
    formatted_discounted_monthly_price: "",
    formatted_discounted_price: "",
    formatted_monthly_price: "",
    formatted_price: "",
    formatted_promo_discount_amount: "",
    formatted_regular_monthly_price: "",
    formatted_regular_price: "",
    has_trial: true,
    is_vip: false,
    ltv: 0,
    lv: 0,
    merchant_account: "noom",
    plan_price: 0,
    price: 0,
    promo_discount_amount: 0,
    regular_price: 0,
    sales_tax_amount: 0,
    starter_fee: 0,
    trial_fee: 1,
    noom_plan_id: "",
    trial_duration: 0,
  },
  plans: {},
};

const serverContextSlice = createSlice({
  name: "serverContext",
  initialState,
  reducers: {
    // NOTE(patrick): ServerContext should only ever be updated as a whole from
    // the backend. Do not mutate serverContext!
    updateServerContext(state, action: PayloadAction<ServerContextState>) {
      return { ...state, ...action.payload };
    },
  },
});

export const { updateServerContext } = serverContextSlice.actions;

export default serverContextSlice.reducer;
